import React, { useState } from "react";
import { Button } from "./ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Check, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Iter56 — Shared bulk toolbar. Renders "(N) selected" + optional Approve +
 * Delete buttons + Deselect. Handles the confirmation dialogs + API calls
 * against the standard `/api/{resource}/bulk-approve` and `/bulk-delete`
 * endpoints.
 *
 * Props:
 *   resource        — e.g. "contracts", "wage-rates", "leave/requests"
 *   selectedIds     — Set<string> or array of ids
 *   onClear         — called after action succeeds; usually the useBulkSelection.clear
 *   onRefresh       — called after action succeeds; usually the page's load()
 *   entityLabel     — "record" | "contract" | "task" (used in dialog copy)
 *   canApprove      — bool — hide approve button when the resource is delete-only
 *   approveLabel    — override the button text (e.g. "Mark done" for Tasks)
 */
export default function BulkActionsBar({
    resource,
    selectedIds,
    onClear,
    onRefresh,
    entityLabel = "record",
    canApprove = true,
    approveLabel = "Approve",
    approvingLabel = "Approving…",
    approveVerb = "approved",
    deletingLabel = "Deleting…",
}) {
    const ids = Array.isArray(selectedIds) ? selectedIds : Array.from(selectedIds || []);
    const count = ids.length;

    const [approveOpen, setApproveOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [busy, setBusy] = useState(null); // "approve" | "delete" | null

    if (count === 0) return null;

    const plural = (n, s) => `${n} ${s}${n === 1 ? "" : "s"}`;

    const runApprove = async () => {
        setBusy("approve");
        try {
            const { data } = await api.post(`/${resource}/bulk-approve`, { ids });
            const ok = data.approved ?? 0;
            const skipped = data.skipped ?? 0;
            let msg = `${approveVerb.charAt(0).toUpperCase()}${approveVerb.slice(1)} ${plural(ok, entityLabel)}.`;
            if (skipped) msg += ` Skipped ${skipped} (invalid state).`;
            toast.success(msg);
            setApproveOpen(false);
            onClear && onClear();
            onRefresh && onRefresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const runDelete = async () => {
        setBusy("delete");
        try {
            const { data } = await api.post(`/${resource}/bulk-delete`, { ids });
            const del = data.deleted ?? 0;
            const skipped = data.skipped ?? 0;
            let msg = `Deleted ${plural(del, entityLabel)}.`;
            if (skipped) msg += ` Skipped ${skipped}.`;
            toast.success(msg);
            setDeleteOpen(false);
            onClear && onClear();
            onRefresh && onRefresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    return (
        <>
            <div
                className="flex items-center gap-2 px-3 py-2 rounded-lg border sticky top-0 z-10"
                style={{ background: "var(--asl-navy-50)", borderColor: "var(--asl-border)" }}
                data-testid="bulk-toolbar"
            >
                <span className="text-xs font-bold asl-navy-text" data-testid="bulk-selected-count">
                    ({count}) selected
                </span>
                <div className="flex-1" />
                {canApprove && (
                    <Button
                        size="sm"
                        className="h-7 text-xs asl-navy-bg text-white"
                        onClick={() => setApproveOpen(true)}
                        disabled={busy !== null}
                        data-testid="bulk-approve-btn"
                    >
                        <Check className="h-3 w-3 mr-1" />
                        {approveLabel} {count}
                    </Button>
                )}
                <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                    onClick={() => setDeleteOpen(true)}
                    disabled={busy !== null}
                    data-testid="bulk-delete-btn"
                >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete {count}
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={onClear}
                    data-testid="bulk-deselect-btn"
                >
                    <X className="h-3 w-3 mr-1" />
                    Deselect all
                </Button>
            </div>

            <AlertDialog open={approveOpen} onOpenChange={setApproveOpen}>
                <AlertDialogContent data-testid="bulk-confirm-approve-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{approveLabel} {plural(count, entityLabel)}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {approveLabel} the selected {plural(count, entityLabel)}. Rows in an
                            invalid state (already {approveVerb}, deleted, etc.) will be skipped.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="bulk-confirm-approve-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={runApprove}
                            disabled={busy === "approve"}
                            data-testid="bulk-confirm-approve-ok"
                        >
                            {busy === "approve" ? approvingLabel : `${approveLabel} ${count}`}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent data-testid="bulk-confirm-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete {plural(count, entityLabel)}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Soft-delete the selected {plural(count, entityLabel)}. Admins can restore
                            them from the deleted-rows view.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="bulk-confirm-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={runDelete}
                            disabled={busy === "delete"}
                            className="bg-rose-600 hover:bg-rose-700"
                            data-testid="bulk-confirm-delete-ok"
                        >
                            {busy === "delete" ? deletingLabel : `Delete ${count}`}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
